/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import AttributeDisplayList from "../AttributeDisplays/AttributeDisplayList";
import { CallHistory } from "../CallHistory/CallHistory";
import ContactDetails from "../ContactDetails/ContactDetails";
import ContactNotes from "../Notes/ContactNotes";
import { ExternalTransfers } from "../ExternalTransfers/ExternalTransfers";
import CannedResponses from "../Responses/CannedResponses";
import { addFrame, removeFrame } from "../../services/IFrameService";
import "./TabLayout.css";
import { RealtimeSentimentAnalysis } from "../RealtimeSentimentAnalysis/RealtimeSentimentAnalysis";
import OutboundSMSNotification from "../OutboundSMSNotification/OutboundSMSNotification";
import Email from "../Email/Email";
import VoicemailTab from "../VoicemailTab/VoicemailTab";
import VideoMeetingTab from "../VideoMeeting/MeetingTabContent";

const NoContentTabPlaceholder = ({ text = "Tab contents will show when a contact is active." }) => (
	<div style={{ flex: 1, display: "flex" }}>
		<div style={{ color: "grey", margin: "auto", textAlign: "center" }}>
			<div>{text}</div>
		</div>
	</div>
);

// Component to render in General tab
export const GeneralTabContent = ({ attributes, contact, caller }) =>
	contact ? (
		<Row noGutters style={{ padding: "5px" }}>
			{attributes?.length ? (
				<>
					<Col lg={12} xl={6} style={{ padding: "10px" }}>
						<ContactDetails
							queue={contact ? contact.queue : null}
							contactType={contact && contact.type}
							phone={caller}
						/>
					</Col>
					<Col lg={12} xl={6} style={{ padding: "10px" }}>
						<AttributeDisplayList attributes={attributes} />
					</Col>
				</>
			) : (
				<Col lg={12} xl={8} style={{ padding: "10px" }}>
					<ContactDetails queue={contact ? contact.queue : null} contactType={contact && contact.type} phone={caller} />
				</Col>
			)}
		</Row>
	) : (
		<NoContentTabPlaceholder />
	);

// Component to render in custom attribute tabs
export const AttributesTabContent = ({ contact, attributes }) => {
	if (!contact) {
		return <NoContentTabPlaceholder />;
	}

	if (!attributes?.length) {
		return <NoContentTabPlaceholder text="No attributes found." />;
	}

	return (
		<Row noGutters style={{ padding: "15px" }}>
			<AttributeDisplayList attributes={attributes} />
		</Row>
	);
};
// Component to render the contact sentiment analysis tab.
export const RealtimeSentimentAnalysisContent = ({ contact, activeTab }) =>
	contact ? (
		<RealtimeSentimentAnalysis contact={contact} enabled={contact && contact.type === "voice"} activeTab={activeTab} />
	) : (
		<NoContentTabPlaceholder />
	);

// Component to render in Contact History tab
export const CallHistoryTabContent = ({ contact, activeTab }) =>
	contact ? (
		<Row noGutters style={{ padding: "15px" }}>
			<CallHistory caller={contact} isPanel={false} activeTab={activeTab} />
		</Row>
	) : (
		<NoContentTabPlaceholder />
	);

// Component to render in Notes tab
export const NotesTabContent = ({ contact, contactNotes, setNotesForContact, renderToastContent }) =>
	contact ? (
		<ContactNotes
			contact={contact}
			contactNote={contactNotes[contact.contactId]}
			setNotesForContact={setNotesForContact}
			renderToastContent={renderToastContent}
		/>
	) : (
		<NoContentTabPlaceholder />
	);
// Component to render in Canned response tab
export const CannedResponsesTabContent = ({ contact }) => (
	<CannedResponses contact={contact} placeholder={<NoContentTabPlaceholder />} />
);

export const OutboundSMSNotificationTabContent = ({ contact, renderToastContent }) => (
	<OutboundSMSNotification contact={contact} renderToastContent={renderToastContent} />
);

// Component to render in External Transfers tab
export const ExternalTransfersTabContent = ({ contact, renderToastContent }) => (
	<ExternalTransfers transfersEnabled={contact && contact.type === "voice"} renderToastContent={renderToastContent} />
);

// Component to render in Email tab
export const EmailTabContent = ({ contact, renderToastContent }) => (
	<Email contact={contact} renderToastContent={renderToastContent} />
);

// Component to render in Voicemail for Queues tab
export const VoicemailTabContent = ({ contact, renderToastContent }) => (
	<VoicemailTab contact={contact} renderToastContent={renderToastContent} />
);

// Component to render in Video meeting
export const VideoMeetingTabContent = ({ attributes }) => <VideoMeetingTab attributes={attributes} />;

// Component to render in Iframe tabs
export const IFrameTabContent = ({ tabConfig }) => {
	const [isLoaded, setIsLoaded] = React.useState(false);

	const tabId = `${tabConfig?.id}-iframe`;
	const tabUrl = tabConfig?.config?.url;

	React.useEffect(() => {
		if (!isLoaded) {
			return;
		}

		addFrame(tabId, tabUrl);

		return () => {
			removeFrame(tabId);
		};
	}, [tabId, tabUrl, isLoaded]);

	return (
		<iframe
			id={tabId}
			style={{ width: "100%", flex: 1, border: "none" }}
			src={tabUrl}
			onLoad={() => setIsLoaded(true)}
			title={tabConfig.description}
		/>
	);
};
